import { Shield, Lock, Key, AlertCircle, FileText, Info } from "lucide-react";

/**
 * CipherSummaryPanel - Displays high-level cipher information during creation
 * 
 * SECURITY NOTE: This panel intentionally does NOT show:
 * - Character set language
 * - Character count
 * - Emoji count
 * - Additional character sets
 * 
 * These details reveal cipher configuration and reduce keyspace/entropy.
 * They are only shown in:
 * 1. Final Review step (before deployment)
 * 2. Cipher Editor (/manage) for cipher owners only
 * 
 * This protects against information disclosure when ciphers are shared.
 */

interface CipherSummaryPanelProps {
  cipherName: string;
  securityLevel: "basic" | "protected" | "verified";
  hasPasscode?: boolean;
}

export default function CipherSummaryPanel({
  cipherName,
  securityLevel,
  hasPasscode,
}: CipherSummaryPanelProps) {
  const getSecurityIcon = () => {
    switch (securityLevel) {
      case "basic":
        return <Shield className="h-5 w-5 text-blue-500" />;
      case "protected":
        return <Lock className="h-5 w-5 text-yellow-500" />;
      case "verified":
        return <Key className="h-5 w-5 text-green-500" />;
    }
  };

  const getSecurityColor = () => {
    switch (securityLevel) {
      case "basic":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "protected":
        // RED when no passcode, yellow/green when passcode is set
        if (!hasPasscode) {
          return "text-red-600 bg-red-50 border-red-300";
        }
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "verified":
        return "text-green-600 bg-green-50 border-green-200";
    }
  };

  const getSecurityDescription = () => {
    switch (securityLevel) {
      case "basic":
        return "Anyone with the cipher code can decrypt messages";
      case "protected":
        return hasPasscode
          ? "4-digit passcode set ✓"
          : "Passcode required — not yet set";
      case "verified":
        return "Requires your approval for each user";
    }
  };

  return (
    <div className="space-y-3">
      {/* Cipher Name */}
      <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-purple-100 shrink-0">
          <FileText className="h-5 w-5 text-purple-600" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500">Cipher Name</p>
          <p className="text-sm font-bold text-gray-900 truncate">
            {cipherName || "Unnamed Cipher"}
          </p>
        </div>
      </div>

      {/* Security Level */}
      <div className={`flex items-center gap-3 p-3 rounded-lg border-2 ${getSecurityColor()}`}>
        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-white/60 shrink-0">
          {getSecurityIcon()}
        </div>
        <div className="min-w-0">
          <p className="text-xs opacity-70">Security Level</p>
          <p className="text-sm font-bold capitalize">{securityLevel}</p>
          <p className="text-xs opacity-80">{getSecurityDescription()}</p>
        </div>
      </div>

      {/* Info Note */}
      <div className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-500 shrink-0" />
        <p className="text-xs text-blue-800">
          Complete all steps to review details and generate your cipher code.
        </p>
      </div>
    </div>
  );
}

